import { __awaiter } from "tslib";
import { ImageFormat, } from "@org-avp/ave-message-factory";
import { constructHtmlImgSrc, isValidFileName, saveImage } from "../utils";
export default class Api {
    constructor(aveApi, cirrusApi) {
        this.aveApi = aveApi;
        this.cirrusApi = cirrusApi;
    }
    loadCar(prCodes, cameraId, environmentId, consumptionData, startingAnimation) {
        return __awaiter(this, void 0, void 0, function* () {
            const startingAnimations = startingAnimation ? [startingAnimation] : undefined;
            yield this.selectCar(prCodes, cameraId, environmentId, consumptionData, startingAnimations);
        });
    }
    changeCar(prCodes, cameraId, consumptionData) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.selectCar(prCodes, cameraId, undefined, consumptionData);
        });
    }
    selectCar(prCodes, cameraId, environmentId, consumptionData, startingAnimations, modelRevision) {
        return __awaiter(this, void 0, void 0, function* () {
            if (environmentId) {
                yield this.changeEnvironment(environmentId);
            }
            yield this.aveApi.selectCar(prCodes, consumptionData, startingAnimations, modelRevision);
            if (cameraId) {
                yield this.changeCamera(cameraId);
            }
        });
    }
    changeCamera(cameraId, movementMode) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.aveApi.changeCamera(cameraId, movementMode);
        });
    }
    changeEnvironment(environmentId) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.aveApi.changeEnvironment(environmentId);
        });
    }
    triggerAnimation(animationId, animationDirection, skipAnimationPlay) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.triggerAnimation(animationId, animationDirection, skipAnimationPlay);
        });
    }
    resetAllAnimations() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.aveApi.resetAllAnimations();
        });
    }
    takeScreenshot() {
        return __awaiter(this, arguments, void 0, function* (imageName = "ave2cloud-screenshot", imageFormat = ImageFormat.jpg, resolution = { width: 1920, height: 1080 }, oversampling = 1, skipDownload = false, transparentBackground = false) {
            if (!isValidFileName(imageName)) {
                return Promise.reject(new Error(`The filename=${imageName} is invalid. Only letters, numbers, '-' or '_' are allowed`));
            }
            const { localPath } = yield this.aveApi.renderImage(imageName, imageFormat, resolution, oversampling, transparentBackground);
            const imageData = yield this.cirrusApi.fetchImage(localPath);
            if (!skipDownload) {
                saveImage(imageData, imageName, imageFormat);
            }
            return constructHtmlImgSrc(imageData, imageFormat);
        });
    }
    loadAnimations() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.loadAnimations();
        });
    }
    loadCameras() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.loadCameras();
        });
    }
    loadEnvironments() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.loadEnvironments();
        });
    }
    moveCamera(x_1, y_1, zoom_1) {
        return __awaiter(this, arguments, void 0, function* (x, y, zoom, panX = 0, panY = 0, focalLength = 0) {
            return yield this.aveApi.moveCamera(x, y, zoom, panX, panY, focalLength);
        });
    }
    getAveState() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.getAveState();
        });
    }
    getInteractionSpots() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.getInteractionSpots();
        });
    }
    setAnimationSpotsVisibility(isVisible) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.setAnimationSpotsVisibility(isVisible);
        });
    }
    getAnimationSpots() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.getAnimationSpots();
        });
    }
    enableAnimationUpdate(animationUpdateHandler) {
        if (!animationUpdateHandler) {
            throw new Error("Animation update handler is required for enabling the feature");
        }
        this.aveApi.enableAnimationUpdate(animationUpdateHandler);
    }
    disableAnimationUpdate() {
        this.aveApi.disableAnimationUpdate();
    }
    enableInteractionSpots(handler) {
        return __awaiter(this, void 0, void 0, function* () {
            if (!handler) {
                throw new Error("Interaction spot handler is required for enabling the feature");
            }
            return yield this.aveApi.enableInteractionSpots(handler);
        });
    }
    disableInteractionSpots() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.disableInteractionSpots();
        });
    }
    setCameraMode(cameraMode) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.setCameraMode(cameraMode);
        });
    }
    setSteeringAngle(degrees) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.setSteeringAngle(degrees);
        });
    }
    setCameraDefinition(cameraDefinition) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.setCameraDefinition(cameraDefinition);
        });
    }
    getCurrentCameraDefinition() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.getCurrentCameraDefinition();
        });
    }
    setCarTransform(location, rotation) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.aveApi.setCarTransform(location, rotation);
        });
    }
    resetCamera() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.aveApi.resetCamera();
        });
    }
    startOrResumeCameraOrbit(orbit) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.aveApi.startOrResumeCameraOrbit(orbit);
        });
    }
    modifyCameraOrbit(command) {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.aveApi.modifyCameraOrbit(command);
        });
    }
    stopCameraOrbit() {
        return __awaiter(this, void 0, void 0, function* () {
            yield this.aveApi.stopCameraOrbit();
        });
    }
    listCameraOrbits() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.listCameraOrbits();
        });
    }
    listModelConfigurations() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.listModelConfigurations();
        });
    }
    listDrivingScenes() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.listDrivingScenes();
        });
    }
    startDrivingScene(name, drivingSceneFinishedHandler) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.startDrivingScene(name, drivingSceneFinishedHandler);
        });
    }
    stopDrivingScene() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.stopDrivingScene();
        });
    }
    displayConsumptionData(enabled) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.displayConsumptionData(enabled);
        });
    }
    startSession(startSessionRequestParameters) {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.startSession(startSessionRequestParameters);
        });
    }
    endSession() {
        return __awaiter(this, void 0, void 0, function* () {
            return yield this.aveApi.endSession();
        });
    }
}
//# sourceMappingURL=api.js.map